import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Plus, Edit, Trash2, MessageSquare, AlertCircle, Eye, EyeOff, MapPin, Building2, Globe, Bell, Info, Megaphone, AlertTriangle, Calendar, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BERICHT_TYPES = [
  { value: 'aankondiging', label: 'Aankondiging', emoji: '📢', color: 'bg-blue-100 text-blue-700', icon: Megaphone },
  { value: 'seizoensmelding', label: 'Seizoensmelding', emoji: '⚠️', color: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
  { value: 'herinnering', label: 'Herinnering', emoji: '🔔', color: 'bg-purple-100 text-purple-700', icon: Bell },
  { value: 'informatie', label: 'Informatie', emoji: 'ℹ️', color: 'bg-slate-100 text-slate-700', icon: Info },
];

const PRIORITEITEN = [
  { value: 'normaal', label: 'Normaal', color: 'bg-slate-100 text-slate-700' },
  { value: 'hoog', label: 'Hoog', color: 'bg-amber-100 text-amber-700' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-700' },
];

import { LOCATIE_TYPES, formatLocatieCount, typeAangevinkt, typeToggle } from '@/lib/locatieTypes';

const Berichtenbeheer = () => {
  const [berichten, setBerichten] = useState([]);
  const [locaties, setLocaties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBericht, setEditingBericht] = useState(null);
  const [filterType, setFilterType] = useState('alle');
  const [showExpired, setShowExpired] = useState(false);

  useEffect(() => {
    fetchBerichten();
    fetchLocaties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showExpired]);

  const fetchBerichten = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/berichten?include_expired=${showExpired}`);
      setBerichten(response.data);
    } catch (error) {
      console.error('Error fetching berichten:', error);
      toast.error('Fout bij ophalen berichten');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocaties = async () => {
    try {
      const response = await axios.get(`${API}/locaties`);
      setLocaties(response.data);
    } catch (error) {
      console.error('Error fetching locaties:', error);
    }
  };

  const handleAdd = () => {
    setEditingBericht(null);
    setShowModal(true);
  };

  const handleEdit = (bericht) => {
    setEditingBericht(bericht);
    setShowModal(true);
  };

  const handleDelete = async (berichtId, berichtTitel) => {
    if (!window.confirm(`Weet je zeker dat je "${berichtTitel}" wilt verwijderen?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/berichten/${berichtId}`);
      toast.success('Bericht verwijderd');
      fetchBerichten();
    } catch (error) {
      console.error('Error deleting bericht:', error);
      toast.error('Fout bij verwijderen');
    }
  };

  const handleToggleActief = async (bericht) => {
    try {
      await axios.put(`${API}/berichten/${bericht._id}`, {
        actief: !bericht.actief
      });
      toast.success(bericht.actief ? 'Bericht gedeactiveerd' : 'Bericht geactiveerd');
      fetchBerichten();
    } catch (error) {
      console.error('Error toggling actief:', error);
      toast.error('Fout bij bijwerken');
    }
  };

  // Filter berichten
  const filteredBerichten = berichten.filter(b => {
    if (filterType !== 'alle' && b.type !== filterType) return false;
    return true;
  });

  // Stats
  const stats = {
    totaal: berichten.length,
    actief: berichten.filter(b => b.actief).length,
    urgent: berichten.filter(b => b.prioriteit === 'urgent' && b.actief).length,
    aankondiging: berichten.filter(b => b.type === 'aankondiging').length,
    seizoensmelding: berichten.filter(b => b.type === 'seizoensmelding').length,
    herinnering: berichten.filter(b => b.type === 'herinnering').length,
    informatie: berichten.filter(b => b.type === 'informatie').length,
  };

  const getTypeInfo = (type) => BERICHT_TYPES.find(t => t.value === type) || BERICHT_TYPES[3];
  const getPriorityInfo = (prioriteit) => PRIORITEITEN.find(p => p.value === prioriteit) || PRIORITEITEN[0];

  const isExpired = (eindDatum) => {
    if (!eindDatum) return false;
    return new Date(eindDatum) < new Date();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-slate-600">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Berichtenbeheer</h1>
          <p className="text-slate-500">Beheer berichten en aankondigingen voor locaties</p>
        </div>
        <Button onClick={handleAdd}>
          <Plus className="w-4 h-4 mr-2" />
          Nieuw Bericht
        </Button>
      </div>

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm text-blue-900">
                <strong>Admin functie:</strong> Berichten die hier worden aangemaakt zijn zichtbaar 
                voor locaties op hun Overzicht pagina. Gebruik prioriteit &quot;Urgent&quot; voor belangrijke 
                mededelingen die direct aandacht nodig hebben.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-slate-800">{stats.totaal}</div>
              <div className="text-sm text-slate-500">Totaal</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-emerald-600">{stats.actief}</div>
              <div className="text-sm text-slate-500">Actief</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{stats.urgent}</div>
              <div className="text-sm text-slate-500">Urgent</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="flex justify-center gap-2">
                <span className="text-sm">📢 {stats.aankondiging}</span>
                <span className="text-sm">⚠️ {stats.seizoensmelding}</span>
                <span className="text-sm">🔔 {stats.herinnering}</span>
              </div>
              <div className="text-sm text-slate-500 mt-1">Per type</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Label className="text-sm">Type:</Label>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Alle types</SelectItem>
              {BERICHT_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  {type.emoji} {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="showExpired"
            checked={showExpired}
            onCheckedChange={setShowExpired}
          />
          <Label htmlFor="showExpired" className="text-sm cursor-pointer">
            Toon verlopen berichten
          </Label>
        </div>
      </div>

      {/* Berichten Table */}
      {filteredBerichten.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <MessageSquare className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-500 mb-4">Nog geen berichten</p>
            <Button onClick={handleAdd}>Eerste bericht aanmaken</Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Bericht</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Prioriteit</TableHead>
                  <TableHead>Toepassing</TableHead>
                  <TableHead>Periode</TableHead>
                  <TableHead>Gelezen</TableHead>
                  <TableHead>Actief</TableHead>
                  <TableHead className="text-right">Acties</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBerichten.map((bericht) => {
                  const typeInfo = getTypeInfo(bericht.type);
                  const priorityInfo = getPriorityInfo(bericht.prioriteit);
                  const expired = isExpired(bericht.eindDatum);
                  
                  return (
                    <TableRow key={bericht._id} className={expired ? 'opacity-50' : ''}>
                      <TableCell>
                        <div>
                          <span className="font-medium">{bericht.titel}</span>
                          {expired && (
                            <Badge variant="outline" className="ml-2 text-xs">Verlopen</Badge>
                          )}
                        </div>
                        {bericht.inhoud && (
                          <p className="text-xs text-slate-500 mt-0.5 truncate max-w-xs">
                            {bericht.inhoud.substring(0, 60)}...
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={typeInfo.color}>
                          {typeInfo.emoji} {typeInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={priorityInfo.color}>
                          {priorityInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <ToepassingBadge
                          toepassingType={bericht.toepassingType}
                          specifiekLocaties={bericht.specifiekLocaties}
                          locatieTypes={bericht.locatieTypes}
                          locaties={locaties}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <Calendar className="w-3 h-3" />
                          {formatDate(bericht.startDatum)} - {formatDate(bericht.eindDatum)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Users className="w-3 h-3 text-slate-400" />
                          <span className="text-sm">{bericht.aantalGelezen || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={bericht.actief}
                            onCheckedChange={() => handleToggleActief(bericht)}
                            className="scale-75"
                          />
                          <span className={`text-sm ${bericht.actief ? 'text-emerald-600' : 'text-slate-400'}`}>
                            {bericht.actief ? 'Actief' : 'Inactief'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(bericht)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(bericht._id, bericht.titel)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <BerichtModal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingBericht(null);
        }}
        bericht={editingBericht}
        onUpdate={fetchBerichten}
        locaties={locaties}
      />
    </div>
  );
};

// Toepassing Badge Component (reused from Takenbeheer)
const ToepassingBadge = ({ toepassingType, specifiekLocaties, locatieTypes, locaties }) => {
  const type = toepassingType || 'alle';

  if (type === 'alle') {
    return (
      <Badge className="bg-emerald-100 text-emerald-700 gap-1">
        <Globe className="w-3 h-3" />
        Alle locaties
      </Badge>
    );
  }

  if (type === 'locaties') {
    const count = specifiekLocaties?.length || 0;
    const locatieNamen = locaties
      .filter(l => specifiekLocaties?.includes(l._id))
      .map(l => l.naam)
      .slice(0, 3);
    const remaining = count - locatieNamen.length;

    return (
      <div className="flex flex-col gap-1">
        <Badge className="bg-blue-100 text-blue-700 gap-1">
          <MapPin className="w-3 h-3" />
          {count} {count === 1 ? 'locatie' : 'locaties'}
        </Badge>
        <span className="text-xs text-slate-500">
          {locatieNamen.join(', ')}
          {remaining > 0 && ` +${remaining}`}
        </span>
      </div>
    );
  }

  if (type === 'locatietypes') {
    const types = locatieTypes || [];
    return (
      <Badge className="bg-orange-100 text-orange-700 gap-1">
        <Building2 className="w-3 h-3" />
        {types.map(t => t.charAt(0).toUpperCase() + t.slice(1)).join(', ')}
      </Badge>
    );
  }

  return <span className="text-slate-400">-</span>;
};

const BerichtModal = ({ open, onClose, bericht, onUpdate, locaties }) => {
  const today = new Date().toISOString().split('T')[0];
  const nextMonth = new Date();
  nextMonth.setMonth(nextMonth.getMonth() + 1);
  const defaultEndDate = nextMonth.toISOString().split('T')[0];

  const [formData, setFormData] = useState({
    titel: '',
    inhoud: '',
    type: 'aankondiging',
    prioriteit: 'normaal',
    startDatum: today,
    eindDatum: defaultEndDate,
    toepassingType: 'alle',
    specifiekLocaties: [],
    locatieTypes: [],
    actief: true
  });

  const [lastOpenState, setLastOpenState] = useState(false);

  // Update form data when modal opens
  if (open && !lastOpenState) {
    Promise.resolve().then(() => {
      const newFormData = bericht ? {
        titel: bericht.titel || '',
        inhoud: bericht.inhoud || '',
        type: bericht.type || 'aankondiging',
        prioriteit: bericht.prioriteit || 'normaal',
        startDatum: bericht.startDatum?.split('T')[0] || today,
        eindDatum: bericht.eindDatum?.split('T')[0] || defaultEndDate,
        toepassingType: bericht.toepassingType || 'alle',
        specifiekLocaties: bericht.specifiekLocaties || [],
        locatieTypes: bericht.locatieTypes || [],
        actief: bericht.actief !== undefined ? bericht.actief : true
      } : {
        titel: '',
        inhoud: '',
        type: 'aankondiging',
        prioriteit: 'normaal',
        startDatum: today,
        eindDatum: defaultEndDate,
        toepassingType: 'alle',
        specifiekLocaties: [],
        locatieTypes: [],
        actief: true
      };
      setFormData(newFormData);
      setLastOpenState(true);
    });
  } else if (!open && lastOpenState) {
    Promise.resolve().then(() => {
      setLastOpenState(false);
    });
  }

  const handleSubmit = async () => {
    if (!formData.titel) {
      toast.error('Titel is verplicht');
      return;
    }
    if (!formData.inhoud) {
      toast.error('Inhoud is verplicht');
      return;
    }
    if (!formData.startDatum || !formData.eindDatum) {
      toast.error('Start- en einddatum zijn verplicht');
      return;
    }
    if (new Date(formData.startDatum) > new Date(formData.eindDatum)) {
      toast.error('Startdatum moet voor einddatum liggen');
      return;
    }
    if (formData.toepassingType === 'locaties' && formData.specifiekLocaties.length === 0) {
      toast.error('Selecteer minimaal 1 locatie');
      return;
    }
    if (formData.toepassingType === 'locatietypes' && formData.locatieTypes.length === 0) {
      toast.error('Selecteer minimaal 1 locatietype');
      return;
    }

    try {
      const dataToSend = {
        ...formData,
        specifiekLocaties: formData.toepassingType === 'locaties' ? formData.specifiekLocaties : [],
        locatieTypes: formData.toepassingType === 'locatietypes' ? formData.locatieTypes : []
      };

      if (bericht?._id) {
        await axios.put(`${API}/berichten/${bericht._id}`, dataToSend);
        toast.success('Bericht bijgewerkt');
      } else {
        await axios.post(`${API}/berichten`, dataToSend);
        toast.success('Bericht aangemaakt');
      }
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error saving bericht:', error);
      toast.error('Fout bij opslaan');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>
            {bericht?._id ? 'Bericht bewerken' : 'Nieuw bericht aanmaken'}
          </DialogTitle>
          <DialogDescription>
            Dit bericht wordt zichtbaar voor de geselecteerde locaties
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-4 py-4 px-1">
          {/* Titel */}
          <div>
            <Label>Titel *</Label>
            <Input
              value={formData.titel}
              onChange={(e) => setFormData({ ...formData, titel: e.target.value })}
              placeholder="Bijv. Belangrijke update winterseizoen"
            />
          </div>

          {/* Inhoud */}
          <div>
            <Label>Inhoud *</Label>
            <Textarea
              value={formData.inhoud}
              onChange={(e) => setFormData({ ...formData, inhoud: e.target.value })}
              placeholder="Schrijf hier het bericht..."
              rows={4}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Type */}
            <div>
              <Label>Type *</Label>
              <Select
                value={formData.type}
                onValueChange={(v) => setFormData({ ...formData, type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BERICHT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.emoji} {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Prioriteit */}
            <div>
              <Label>Prioriteit *</Label>
              <Select
                value={formData.prioriteit}
                onValueChange={(v) => setFormData({ ...formData, prioriteit: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITEITEN.map(p => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.prioriteit === 'urgent' && (
                <p className="text-xs text-red-600 mt-1">⚠️ Urgente berichten worden prominent getoond</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Start datum */}
            <div>
              <Label>Startdatum *</Label>
              <Input
                type="date"
                value={formData.startDatum}
                onChange={(e) => setFormData({ ...formData, startDatum: e.target.value })}
              />
              <p className="text-xs text-slate-500 mt-1">Vanaf wanneer is het bericht zichtbaar?</p>
            </div>

            {/* Eind datum */}
            <div>
              <Label>Einddatum *</Label>
              <Input
                type="date"
                value={formData.eindDatum}
                onChange={(e) => setFormData({ ...formData, eindDatum: e.target.value })}
              />
              <p className="text-xs text-slate-500 mt-1">Tot wanneer is het bericht zichtbaar?</p>
            </div>
          </div>

          {/* Toepassing Section */}
          <div className="border-t pt-4">
            <Label className="text-base font-semibold mb-3 block">Toepassing</Label>
            <p className="text-sm text-slate-500 mb-4">Voor welke locaties is dit bericht zichtbaar?</p>

            <RadioGroup
              value={formData.toepassingType}
              onValueChange={(v) => setFormData({ ...formData, toepassingType: v })}
              className="space-y-3"
            >
              {/* Alle locaties */}
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="alle" id="alle" className="mt-1" />
                <div>
                  <Label htmlFor="alle" className="font-medium flex items-center gap-2 cursor-pointer">
                    <Globe className="w-4 h-4 text-emerald-600" />
                    Alle locaties
                  </Label>
                  <p className="text-xs text-slate-500">Bericht is zichtbaar voor alle 20 locaties</p>
                </div>
              </div>

              {/* Specifieke locaties */}
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="locaties" id="locaties" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="locaties" className="font-medium flex items-center gap-2 cursor-pointer">
                    <MapPin className="w-4 h-4 text-blue-600" />
                    Specifieke locaties
                  </Label>
                  <p className="text-xs text-slate-500">Kies specifieke locaties</p>
                </div>
              </div>

              {formData.toepassingType === 'locaties' && (
                <div className="pl-6 border-l-2 border-blue-200 ml-4">
                  <Label className="text-sm mb-2 block">Selecteer locaties:</Label>
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 bg-slate-50 rounded-lg">
                    {locaties.map((loc) => (
                      <div key={loc._id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`loc-${loc._id}`}
                          checked={formData.specifiekLocaties.includes(loc._id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({
                                ...formData,
                                specifiekLocaties: [...formData.specifiekLocaties, loc._id]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                specifiekLocaties: formData.specifiekLocaties.filter(id => id !== loc._id)
                              });
                            }
                          }}
                        />
                        <Label htmlFor={`loc-${loc._id}`} className="text-sm cursor-pointer">
                          {loc.naam}
                        </Label>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    {formData.specifiekLocaties.length} van {locaties.length} geselecteerd
                  </p>
                </div>
              )}

              {/* Per locatietype */}
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="locatietypes" id="locatietypes" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="locatietypes" className="font-medium flex items-center gap-2 cursor-pointer">
                    <Building2 className="w-4 h-4 text-orange-600" />
                    Per locatietype
                  </Label>
                  <p className="text-xs text-slate-500">Alle locaties van een bepaald type</p>
                </div>
              </div>

              {formData.toepassingType === 'locatietypes' && (
                <div className="pl-6 border-l-2 border-orange-200 ml-4">
                  <Label className="text-sm mb-2 block">Selecteer locatietypes:</Label>
                  <div className="space-y-2 p-2 bg-slate-50 rounded-lg">
                    {LOCATIE_TYPES.map((type) => (
                      <div key={type.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={`type-${type.value}`}
                          checked={typeAangevinkt(formData.locatieTypes, type.value)}
                          onCheckedChange={(checked) => setFormData({
                            ...formData,
                            // 6.21: wasstraat = truckwash + legacy wasstraat
                            locatieTypes: typeToggle(formData.locatieTypes, type.value, !!checked)
                          })}
                          data-testid={`locatietype-${type.value}`}
                        />
                        <Label htmlFor={`type-${type.value}`} className="text-sm cursor-pointer">
                          {type.label} ({formatLocatieCount(locaties, type.value)})
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </RadioGroup>
          </div>

          {/* Actief toggle */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Actief</Label>
                <p className="text-xs text-slate-500">Inactieve berichten worden niet getoond</p>
              </div>
              <Switch
                checked={formData.actief}
                onCheckedChange={(v) => setFormData({ ...formData, actief: v })}
              />
            </div>
          </div>
        </div>
        <DialogFooter className="flex-shrink-0 border-t pt-4 mt-2">
          <Button variant="outline" onClick={onClose}>
            Annuleren
          </Button>
          <Button onClick={handleSubmit}>
            {bericht?._id ? 'Opslaan' : 'Aanmaken'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Berichtenbeheer;
